import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Bell, BellOff, BellRing, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Convert base64 to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * PushNotificationManager - Handles browser push notification subscriptions
 * Shows a prompt to enable notifications for exhibitors and reps
 */
const PushNotificationManager = () => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  // Fetch VAPID public key from backend and check support
  useEffect(() => {
    const init = async () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      
      if (!supported) return;

      setPermission(Notification.permission);
      
      // Check if already subscribed
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (e) {
        console.warn('[Push] Could not check subscription:', e);
      }

      // Fetch VAPID key from backend
      try {
        const res = await axios.get(`${API}/push/vapid-key`);
        setVapidKey(res.data.public_key);
      } catch (e) {
        console.warn('[Push] VAPID key not available:', e.response?.status);
      }
    };
    
    init();
  }, []);

  // Show prompt for exhibitors and reps who haven't subscribed
  useEffect(() => {
    if (!user || !isSupported || !vapidKey) return;
    
    // Only show for sellers (exhibitors) and reps
    if ((user.role === 'seller' || user.role === 'rep') && 
        permission === 'default' && 
        !isSubscribed) {
      // Delay showing prompt to not interrupt user flow
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user, isSupported, permission, isSubscribed, vapidKey]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidKey) {
      toast.error('Push notifications are not available');
      return;
    }

    setLoading(true);
    try {
      // Request permission
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result !== 'granted') {
        toast.error(t('notifications.permission_denied', 'Notification permission denied'));
        setShowPrompt(false);
        setLoading(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      });

      setIsSubscribed(true);
      setShowPrompt(false);
      toast.success(t('notifications.enabled', 'Push notifications enabled! You\'ll receive alerts for new requests.'));
      
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      if (error.name === 'InvalidStateError') {
        toast.error('Push notification setup failed. Please try refreshing the page.');
      } else {
        toast.error('Failed to enable notifications. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [isSupported, vapidKey, t]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Notify backend
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: subscription.endpoint
        }).catch(() => {}); // Ignore errors
        
        // Unsubscribe locally
        await subscription.unsubscribe();
      }
      
      setIsSubscribed(false);
      toast.success(t('notifications.disabled', 'Push notifications disabled'));
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Send a test notification
  const sendTestNotification = useCallback(async () => {
    if (!isSubscribed) {
      toast.error('Please enable notifications first');
      return;
    }
    
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Test notification sent!');
    } catch (error) {
      // Fallback: show local notification
      if (Notification.permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        registration.showNotification('TradeHub24 Test', {
          body: 'Push notifications are working!',
          icon: '/tradehub_logo.png',
          badge: '/tradehub_logo.png'
        });
      }
    }
  }, [isSubscribed]);

  // Don't render if not supported, no VAPID key, or user is not seller/rep
  if (!isSupported || !vapidKey || !user || (user.role !== 'seller' && user.role !== 'rep')) {
    return null;
  }

  // Notification prompt banner
  if (showPrompt && !isSubscribed && permission === 'default') {
    return (
      <div data-testid="push-notification-prompt" className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm bg-white border border-slate-200 rounded-lg shadow-lg p-4 z-50 animate-in slide-in-from-bottom-4">
        <button 
          data-testid="push-notification-dismiss"
          onClick={() => setShowPrompt(false)}
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-full">
            <BellRing className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-slate-900 text-sm">
              {t('notifications.enable_title', 'Enable Notifications')}
            </h4>
            <p className="text-xs text-slate-600 mt-1">
              {t('notifications.enable_description', 'Get instant alerts when buyers send requests, even when you\'re not on this page.')}
            </p>
            <div className="flex gap-2 mt-3">
              <Button data-testid="push-notification-enable" size="sm" onClick={subscribe} disabled={loading}>
                {loading ? t('notifications.enabling', 'Enabling...') : t('notifications.enable', 'Enable')}
              </Button>
              <Button data-testid="push-notification-later" size="sm" variant="ghost" onClick={() => setShowPrompt(false)}>
                {t('notifications.later', 'Later')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Export helper for manual notification control
export const usePushNotifications = () => {
  const { user } = useContext(AuthContext);
  const [isSupported] = useState('PushManager' in window);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported) return;
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (e) {
        // Silently fail
      }
    };
    checkSubscription();
  }, [isSupported]);

  return {
    isSupported,
    isSubscribed,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'default'
  };
};

export default PushNotificationManager;
